import { FileText } from "lucide-react";
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import { useGetOrganizationSettings } from "~/api/organizations/organizations";
import logoImage from "~/assets/logo.svg";
import { UnifiedMarkdown } from "~/components/UnifiedMarkdown";
import { Button } from "~/components/ui/button";
import type { SectionView } from "~/features/cases/hooks/useFtueTemplateSectionsView";
import {
  FONT_FAMILY_STACKS,
  isFontFamilyOptionKey,
} from "~/features/settings/constants";
import { useOrganizationParams } from "~/helpers";
import { cn } from "~/lib/utils";

const PLACEHOLDER_LINE_WIDTHS = [98, 92, 96, 84, 90, 70];

/** Maximum number of pages shown before the gradient cutoff. */
const MAX_VISIBLE_PAGES = 20;

/** Fixed page dimensions for the document page view (px). */
const PAGE_CONTENT_WIDTH = 640;
const PAGE_ASPECT_RATIO = 1.414;
const PAGE_HEIGHT = Math.round(PAGE_CONTENT_WIDTH * PAGE_ASPECT_RATIO);
const PAGE_PADDING_X = 48;
const PAGE_PADDING_Y = 32;
const HEADER_HEIGHT = 80;
const FOOTER_HEIGHT = 40;
/** Container heights (border-box — padding is inside this value). */
const CONTENT_AREA_FIRST = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
const CONTENT_AREA_OTHER = PAGE_HEIGHT - FOOTER_HEIGHT;
/** Actual visible content heights after subtracting vertical padding. */
const VISIBLE_CONTENT_FIRST = CONTENT_AREA_FIRST - PAGE_PADDING_Y * 2;
const VISIBLE_CONTENT_OTHER = CONTENT_AREA_OTHER - PAGE_PADDING_Y * 2;

interface FtueReportPreviewProps {
  sections?: SectionView[];
  /** Raw markdown blob rendered as a single document (alternative to `sections`). */
  content?: string;
  onContinue: () => void;
}

export const FtueReportPreview: FC<FtueReportPreviewProps> = ({
  sections,
  content,
  onContinue,
}) => {
  const { organizationId } = useOrganizationParams();
  const { data: settings } = useGetOrganizationSettings(organizationId);

  const previewLogo = settings?.logoUrl ?? logoImage;
  const fontFamily = settings?.fontFamily ?? "serif";
  const lineSpacing = settings?.lineSpacing ?? "1.5";

  const fontFamilyStyle = isFontFamilyOptionKey(fontFamily)
    ? FONT_FAMILY_STACKS[fontFamily]
    : fontFamily;

  const hasContent = !!content && content.trim().length > 0;

  const isReportReady =
    hasContent ||
    ((sections?.length ?? 0) > 0 &&
      (sections?.every(
        (s) => s.status === "completed" && (s.content?.trim().length ?? 0) > 0
      ) ??
        false));

  return (
    <div className="flex flex-col lg:flex-row min-h-ftue px-4 gap-6 max-w-7xl mx-auto w-full">
      {/* Left Panel — Info */}
      <div className="lg:w-1/3 flex flex-col justify-center space-y-6 py-8">
        <div className="space-y-3">
          <h1 className="text-3xl font-semibold text-foreground">
            {isReportReady ? (
              <FormattedMessage
                id="ftue.reportPreview.title"
                defaultMessage="Your Template Is Ready!"
              />
            ) : (
              <FormattedMessage
                id="ftue.reportPreview.generating.title"
                defaultMessage="Generating Your Sample Report…"
              />
            )}
          </h1>
          <p className="text-lg text-muted-foreground">
            {isReportReady ? (
              <FormattedMessage
                id="ftue.reportPreview.subtitle"
                defaultMessage="Here's a taste of what you'll get for each new case — ready to review and send to your clients."
              />
            ) : (
              <FormattedMessage
                id="ftue.reportPreview.generating.subtitle"
                defaultMessage="Filling in your template section by section. The preview will update as each one finishes."
              />
            )}
          </p>
        </div>

        <p className="text-muted-foreground">
          <FormattedMessage
            id="ftue.reportPreview.uploadCta"
            defaultMessage="Upload your case documents to get a full report — formatted, cited, and ready to deliver to your clients."
          />
        </p>

        <Button size="lg" onClick={onContinue} className="mt-4">
          <FormattedMessage
            id="ftue.reportPreview.continue"
            defaultMessage="Continue"
          />
        </Button>
      </div>

      {/* Right Panel — Paginated Report Viewer */}
      <div className="lg:w-2/3 flex flex-col py-8">
        <div className="flex items-center gap-2 mb-4 text-muted-foreground">
          <FileText className="w-4 h-4" />
          <span className="text-sm font-medium">
            <FormattedMessage
              id="ftue.reportPreview.previewLabel"
              defaultMessage="Report Preview"
            />
          </span>
        </div>

        <ReportPageView
          sections={sections}
          content={hasContent ? content : undefined}
          previewLogo={previewLogo}
          fontFamilyStyle={fontFamilyStyle}
          lineSpacing={lineSpacing}
        />

        <p className="text-xs text-muted-foreground mt-3 text-center">
          <FormattedMessage
            id="ftue.reportPreview.stylingNote"
            defaultMessage="You can add your branding and customize styling later."
          />
        </p>
      </div>
    </div>
  );
};

/* ─── Page-view renderer ────────────────────────────────────────────────── */

interface ReportPageViewProps {
  sections?: SectionView[];
  /** When provided, rendered as a single UnifiedMarkdown block instead of sections. */
  content?: string;
  previewLogo: string;
  fontFamilyStyle: string;
  lineSpacing: string;
}

/**
 * Two-phase renderer:
 * 1. Measurement — render content once inside a single page frame, measure
 *    total scrollHeight via useLayoutEffect (synchronous, invisible to user).
 * 2. Paginated — split the measured content across discrete A4-like pages
 *    with header (page 1 only), footer (every page), and a gradient cutoff
 *    after MAX_VISIBLE_PAGES.
 *
 * In jsdom (tests) scrollHeight is 0, so measurement never completes and
 * the component stays in the single-page fallback — tests pass without
 * duplicate DOM nodes.
 */
const ReportPageView: FC<ReportPageViewProps> = ({
  sections,
  content,
  previewLogo,
  fontFamilyStyle,
  lineSpacing,
}) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const doMeasure = useCallback(() => {
    if (measureRef.current) {
      const h = measureRef.current.scrollHeight;
      if (h > 0) setContentHeight(h);
    }
  }, []);

  useLayoutEffect(doMeasure, [doMeasure, sections, content]);

  const measuredPages = useMemo(() => {
    if (contentHeight <= 0) return 0;
    if (contentHeight <= VISIBLE_CONTENT_FIRST) return 1;
    const remaining = contentHeight - VISIBLE_CONTENT_FIRST;
    return 1 + Math.ceil(remaining / VISIBLE_CONTENT_OTHER);
  }, [contentHeight]);

  const hasMeasured = measuredPages > 0;

  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const observer = new ResizeObserver(doMeasure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [doMeasure, hasMeasured]);
  const totalPages = hasMeasured ? measuredPages : 1;

  const contentStyle = useMemo(
    () => ({
      fontFamily: fontFamilyStyle,
      lineHeight: lineSpacing,
    }),
    [fontFamilyStyle, lineSpacing]
  );

  const pageStyle = useMemo(
    () => ({
      width: PAGE_CONTENT_WIDTH,
      height: PAGE_HEIGHT,
      ...contentStyle,
    }),
    [contentStyle]
  );

  const renderBody = () => {
    if (content) {
      return (
        <UnifiedMarkdown
          className="prose prose-sm dark:prose-invert max-w-none"
          withHeadings
        >
          {content}
        </UnifiedMarkdown>
      );
    }
    return (sections ?? []).map((section, index) => (
      <SectionBlock key={section.id} section={section} isFirst={index === 0} />
    ));
  };

  const renderHeader = () => (
    <div
      className="flex items-start justify-between border-b"
      style={{
        height: HEADER_HEIGHT,
        padding: `${PAGE_PADDING_Y}px ${PAGE_PADDING_X}px 0`,
      }}
    >
      <div className="text-xs text-gray-600 dark:text-muted-foreground">
        <FormattedMessage
          id="settings.preview.caseName"
          defaultMessage="Case Name"
        />
        :{" "}
        <FormattedMessage
          id="settings.preview.exampleCase"
          defaultMessage="Example case"
        />
        <br />
        <FormattedMessage
          id="settings.preview.dateOfBirth"
          defaultMessage="Date of Birth"
        />
        : 3/4/1958
        <br />
        <FormattedMessage
          id="settings.preview.claimNumber"
          defaultMessage="Claim/Case #"
        />
        : R2515575
      </div>
      <img src={previewLogo} alt="Logo" className="h-8 object-contain" />
    </div>
  );

  const renderFooter = (pageNumber: number) => (
    <div
      className="flex items-center justify-between text-xs text-gray-400 dark:text-muted-foreground border-t"
      style={{ height: FOOTER_HEIGHT, padding: `0 ${PAGE_PADDING_X}px` }}
    >
      <span>
        <FormattedMessage
          id="settings.preview.poweredBy"
          defaultMessage="Powered by Sky AI"
        />
      </span>
      <span>
        {pageNumber}{" "}
        <FormattedMessage id="ftue.reportPreview.pageOf" defaultMessage="of" />{" "}
        {totalPages}
      </span>
    </div>
  );

  // ── Fallback: single-page view (also used for measurement) ──
  if (!hasMeasured) {
    return (
      <div
        className="flex-1 overflow-y-auto flex flex-col items-center"
        style={{ maxHeight: "calc(100vh - 260px)" }}
      >
        <div
          className="bg-white dark:bg-card rounded-lg border shadow-sm flex-shrink-0 flex flex-col"
          style={pageStyle}
        >
          {renderHeader()}
          <div
            className="flex-1 overflow-hidden"
            style={{ padding: `${PAGE_PADDING_Y}px ${PAGE_PADDING_X}px` }}
          >
            <div ref={measureRef} className="space-y-6">
              {renderBody()}
            </div>
          </div>
          {renderFooter(1)}
        </div>
      </div>
    );
  }

  // ── Paginated view ──
  const visiblePageCount = Math.min(totalPages, MAX_VISIBLE_PAGES);

  const getContentOffset = (pageIndex: number): number => {
    if (pageIndex === 0) return 0;
    return VISIBLE_CONTENT_FIRST + (pageIndex - 1) * VISIBLE_CONTENT_OTHER;
  };

  return (
    <div
      className="flex-1 overflow-y-auto"
      style={{ maxHeight: "calc(100vh - 260px)" }}
    >
      <div className="relative">
        <div className="flex flex-col items-center gap-6">
          {Array.from({ length: visiblePageCount }).map((_, pageIndex) => {
            const isFirst = pageIndex === 0;
            const areaHeight = isFirst
              ? CONTENT_AREA_FIRST
              : CONTENT_AREA_OTHER;
            const offset = getContentOffset(pageIndex);

            return (
              <div
                key={pageIndex}
                className="bg-white dark:bg-card rounded-lg border shadow-sm flex-shrink-0"
                style={pageStyle}
              >
                {isFirst && renderHeader()}

                <div
                  style={{
                    height: areaHeight,
                    overflow: "hidden",
                    padding: `${PAGE_PADDING_Y}px ${PAGE_PADDING_X}px`,
                  }}
                >
                  <div
                    ref={isFirst ? measureRef : undefined}
                    className="space-y-6"
                    style={{ transform: `translateY(-${offset}px)` }}
                  >
                    {renderBody()}
                  </div>
                </div>

                {renderFooter(pageIndex + 1)}
              </div>
            );
          })}
        </div>

        {/* Gradient overlay when content exceeds MAX_VISIBLE_PAGES */}
        {totalPages > MAX_VISIBLE_PAGES && (
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0"
            style={{ height: PAGE_HEIGHT * 0.6 }}
          >
            <div className="w-full h-full bg-gradient-to-t from-background via-background/80 to-transparent" />
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Section rendering ─────────────────────────────────────────────────── */

interface SectionBlockProps {
  section: SectionView;
  isFirst: boolean;
}

const SectionBlock: FC<SectionBlockProps> = ({ section, isFirst }) => {
  const body = section.content?.trim() ?? "";
  const isCompleted = section.status === "completed" && body.length > 0;

  if (isCompleted) {
    return (
      <UnifiedMarkdown
        className="prose prose-sm dark:prose-invert max-w-none"
        withHeadings
        components={
          isFirst
            ? {
                h1: ({ node: _node, ...props }) => (
                  <h1
                    {...props}
                    className="text-2xl font-bold mb-3 normal-case"
                  />
                ),
              }
            : undefined
        }
      >
        {buildSectionMarkdown(section.title, body, isFirst)}
      </UnifiedMarkdown>
    );
  }

  return <SectionPlaceholder title={section.title} isFirst={isFirst} />;
};

/**
 * Build markdown for one completed section. Single newlines in the body are
 * promoted to paragraph breaks — except inside table blocks, where
 * consecutive lines must stay together for proper GFM table rendering.
 */
function buildSectionMarkdown(
  title: string,
  body: string,
  isFirst: boolean
): string {
  const hash = isFirst ? "#" : "##";
  const trimmedTitle = title.trim();
  const normalizedBody = normalizeBodyNewlines(body);
  if (!trimmedTitle) return normalizedBody;
  return `${hash} ${trimmedTitle}\n\n${normalizedBody}`;
}

/**
 * Promote single newlines to paragraph breaks while preserving table blocks.
 * Markdown tables require rows on consecutive lines; doubling newlines inside
 * them would break rendering.
 */
function normalizeBodyNewlines(body: string): string {
  const lines = body.split("\n");
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const nextLine = lines[i + 1] as string | undefined;
    result.push(line);

    if (nextLine === undefined) continue;

    const currentIsTable = isTableLine(line);
    const nextIsTable = isTableLine(nextLine);

    if (currentIsTable && nextIsTable) {
      continue;
    }

    if (line === "" || nextLine === "") {
      continue;
    }

    result.push("");
  }

  return result.join("\n");
}

const TABLE_LINE_REGEX = /^\s*\|/;
function isTableLine(line: string): boolean {
  return TABLE_LINE_REGEX.test(line);
}

/* ─── Section placeholder ───────────────────────────────────────────────── */

interface SectionPlaceholderProps {
  title: string;
  isFirst: boolean;
}

const SectionPlaceholder: FC<SectionPlaceholderProps> = ({
  title,
  isFirst,
}) => {
  const HeadingTag = isFirst ? "h1" : "h2";
  return (
    <div
      data-testid="ftue-report-preview-section-placeholder"
      aria-live="polite"
      aria-busy="true"
    >
      <HeadingTag
        className={cn(
          isFirst
            ? "text-2xl font-bold normal-case"
            : "text-xl font-semibold normal-case",
          "mb-3 text-muted-foreground/60"
        )}
      >
        {title}
      </HeadingTag>
      <div className="space-y-2.5" aria-hidden="true">
        {PLACEHOLDER_LINE_WIDTHS.map((w, i) => (
          <div
            key={i}
            className="h-3 rounded bg-muted-foreground/15"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    </div>
  );
};
